'use client';

import { useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { cn } from '@/lib';

import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '.';

// ----------------- Schema builder -----------------
const buildSchema = (fields) => {
  const shape = {};

  fields.forEach((f) => {
    let base = z.string({ required_error: `${f.label} is required` });

    if (!f.required) {
      base = base.optional().transform((v) => v ?? '');
    }

    if (f.type === 'text') {
      let s = base;
      if (f.minLength)
        s = s.min(f.minLength, {
          message: `${f.label} must be at least ${f.minLength} characters`,
        });
      if (f.maxLength)
        s = s.max(f.maxLength, { message: `${f.label} must be at most ${f.maxLength} characters` });
      if (f.pattern) s = s.regex(f.pattern, { message: `${f.label} format is invalid` });
      shape[f.name] = s;
    } else if (f.type === 'radio' || f.type === 'select') {
      const opts = f.options?.map((o) => o.value) || [];
      let s = base.refine((val) => (val === '' && !f.required) || opts.includes(val), {
        message: `Please select a valid option for ${f.label}`,
      });
      shape[f.name] = s;
    } else {
      shape[f.name] = base;
    }
  });

  return z.object(shape);
};

// ----------------- Renderer -----------------
export const FormBuilder = ({
  fields,
  submitLabel = 'Submit',
  schemaOverride,
  onSubmit,
  defaultValues,
  className,
  isLoading = false,
  showResetButton = false,
}) => {
  const schema = useMemo(() => schemaOverride ?? buildSchema(fields), [fields, schemaOverride]);

  const derivedDefaults = useMemo(() => {
    const acc = {};
    fields.forEach((f) => {
      acc[f.name] = defaultValues?.[f.name] ?? f.defaultValue ?? '';
    });
    // console.log('Derived defaults:', acc); // Debug log
    return acc;
  }, [fields, defaultValues]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: derivedDefaults,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    formState: { isSubmitting },
    setError,
    reset,
  } = form;

  // Update form values when defaultValues change
  useEffect(() => {
    // console.log('Resetting form with values:', derivedDefaults); // Debug log
    reset(derivedDefaults);
  }, [derivedDefaults, reset]);

  const handleSubmit = async (values) => {
    await onSubmit?.({ values, setError, reset });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className={cn('w-full space-y-6', className)}
      >
        {fields.map((field) => (
          <FormField
            key={field.name}
            control={form.control}
            name={field.name}
            render={({ field: rhf, fieldState }) => {
              return (
                <FormItem>
                  {field.label && <FormLabel>{field.label}</FormLabel>}
                  {field.description ? (
                    <FormDescription>{field.description}</FormDescription>
                  ) : null}

                  <FormControl>
                    <div>
                      {field.type === 'text' && (
                        <Input {...rhf} placeholder={field.placeholder} disabled={isSubmitting} />
                      )}

                      {field.type === 'select' && (
                        <Select
                          key={`${field.name}-${rhf.value}`} // Force re-render when value changes
                          value={rhf.value || undefined}
                          onValueChange={rhf.onChange}
                        >
                          <SelectTrigger
                            size="lg"
                            className={cn('w-full', field?.className)}
                            disabled={isSubmitting}
                            aria-invalid={fieldState.invalid}
                          >
                            <SelectValue placeholder={field.placeholder ?? 'Select...'} />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        ))}

        <div className="flex justify-end gap-3 pt-2">
          {showResetButton && (
            <Button
              type="button"
              variant="outline"
              onClick={() => reset(derivedDefaults)}
              disabled={isSubmitting || isLoading}
            >
              Reset
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting || isLoading}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
};
